import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Pagination from '@mui/material/Pagination'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import SortIcon from '@mui/icons-material/Sort'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import SearchIcon from '@mui/icons-material/Search'

import styles from './AccountsTable.module.css'
import { Paper } from '@mui/material'

export const DEFAULT_PAGE_SIZE = 10

const FILLER_ACCOUNT = Symbol('FILLER_ACCOUNT')

const NO_FILTER_COLUMN = Symbol('NO_FILTER_COLUMN')
const NO_FILTER_VALUE = ''
const NO_ORDER_COLUMN = Symbol('NO_ORDER_COLUMN')

const ORDER_DIRECTIONS = [
  {
    lodashDirection: undefined,
    icon: <SortIcon fontSize='small' sx={{ verticalAlign: 'sub' }} />
  },
  {
    lodashDirection: 'asc',
    icon: <ArrowUpwardIcon fontSize='small' sx={{ verticalAlign: 'sub' }} />
  },
  {
    lodashDirection: 'desc',
    icon: <ArrowDownwardIcon fontSize='small' sx={{ verticalAlign: 'sub' }} />
  }
]

const nextDirectionIndex = index => (index + 1) % ORDER_DIRECTIONS.length

export const ACCOUNT_ROW_PLACEHOLDER_TESTID = 'accountRowPlaceholder'
export const FILTER_DROPDOWN_TESTID = 'filterDropdown'
export const ACCOUNTS_TABLE_TESTID = 'accountsTable'
export const ACCOUNT_COLUMN_ID_DATA_ATTR = 'data-account-column-id'

/**
 * A paginated table whose rows represent accounts.
 *
 * @param {Object} props
 * @param {Object[]} props.accounts
 *    all Account objects to display (before filtering)
 * @param {AccountsTableColumns~Descriptor[]} props.columns
 *    column descriptors
 * @param {boolean} props.loading
 *    if true, the table displays a loading state; if false, the table displays
 *    the given accounts
 * @param {Object} props.selectedAccount
 *    an Account object to highlight
 * @param onSelectAccount
 *    when the row corresponding to `account` is clicked, AccountsTable calls
 *    `onSelectAccount(account)`
 * @param children
 *    components to be placed in the actions section above the table
 */
export const AccountsTable = ({
  accounts,
  columns,
  loading,
  selectedAccount,
  onSelectAccount,
  children: toolbarActions
}) => {
  const pageSize = DEFAULT_PAGE_SIZE

  const [accountsView, setAccountsView] = useState(accounts)
  const [activePage, setActivePage] = useState(0)
  const [activePageAccounts, setActivePageAccounts] = useState(
    [...Array(pageSize)].fill(FILLER_ACCOUNT)
  )

  const [filterableColumns, setFilterableColumns] = useState([])
  const [filter, setFilter] = useState({
    column: NO_FILTER_COLUMN,
    value: NO_FILTER_VALUE
  })
  const [order, setOrder] = useState({
    column: NO_ORDER_COLUMN,
    directionIndex: 0
  })

  /* eslint-disable brace-style */
  useEffect(() => {
    const filterableColumns = columns.filter(column => column.filtering)
    setFilterableColumns(filterableColumns)

    // Reset filtering state if no columns are filterable
    if (filterableColumns.length === 0) {
      setFilter({
        column: NO_FILTER_COLUMN,
        value: NO_FILTER_VALUE
      })
    }

    // Pick the first filterable column if one is available
    else if (filter.column === NO_FILTER_COLUMN) {
      setFilter(filter => ({ ...filter, column: filterableColumns[0] }))
    }

    // Reset filterColumn if it's no longer among the available columns
    else if (!filterableColumns.includes(filter.column)) {
      setFilter(filter => ({ ...filter, column: NO_FILTER_COLUMN }))
    }
  }, [columns, filter])
  /* eslint-enable brace-style */

  /**
   * Sets `accountsView` to the filtered and sorted subset of `props.accounts`.
   */
  useEffect(() => {
    let view = _(accounts)

    if (filter.value !== '' && filter.column !== NO_FILTER_COLUMN) {
      const filterAccessor = filter.column.filtering.accessor
      if (typeof filterAccessor === 'string') {
        view = view.filter(
          item =>
            !!item[filterAccessor] &&
            item[filterAccessor].toString().includes(filter.value)
        )
      } else if (typeof filterAccessor === 'function') {
        view = view.filter(item => filterAccessor(item).includes(filter.value))
      } else {
        throw new Error(
          `Invalid filtering accessor on column ${filter.column.id}`
        )
      }
    }

    if (order.column !== NO_ORDER_COLUMN) {
      view = view.orderBy(
        [order.column.ordering.iteratee],
        [ORDER_DIRECTIONS[order.directionIndex].lodashDirection]
      )
    }

    setAccountsView(view.value())
  }, [accounts, filter, order])

  /**
   * Returns a page of accounts from `accountView` according to the given page
   * number.
   */
  const computeAccountsPage = useCallback(
    activePage => {
      const start = activePage * pageSize
      const pageItems = accountsView.slice(start, start + pageSize)
      const fillerCount = pageSize - pageItems.length
      if (fillerCount) {
        pageItems.push(...Array(fillerCount).fill(FILLER_ACCOUNT))
      }
      return pageItems
    },
    [accountsView, pageSize]
  )

  const totalPages = Math.ceil(accountsView.length / pageSize)

  const onPageChange = useCallback(
    (_event, newPage) => {
      // MUI Pagination is 1-indexed; convert to 0-indexed
      const newActivePage = newPage - 1
      setActivePage(newActivePage)
      setActivePageAccounts(computeAccountsPage(newActivePage))
      onSelectAccount(undefined)
    },
    [onSelectAccount, computeAccountsPage]
  )

  useEffect(() => {
    loading || onPageChange(undefined, 1)
  }, [accounts, loading, onPageChange])

  const tableRows = _.range(pageSize).map(index => {
    if (loading) {
      return <LoadingAccountRow key={index} columnCount={columns.length} />
    }

    const account = activePageAccounts[index]
    return account === FILLER_ACCOUNT ? (
      <FillerAccountRow key={index} columnCount={columns.length} />
    ) : (
      <AccountRow
        account={account}
        columns={columns}
        isSelected={account === selectedAccount}
        onSelect={onSelectAccount}
        key={index}
      />
    )
  })

  const filterColumnDropdownOptions = filterableColumns.map(({ title, id }) => ({
    label: title,
    value: id
  }))

  const onFilterColumnDropdownChange = event =>
    setFilter(filter => ({
      ...filter,
      column:
        filterableColumns.find(column => column.id === event.target.value) ||
        NO_FILTER_COLUMN
    }))
  const onSearchInputChange = event =>
    setFilter(filter => ({ ...filter, value: event.target.value }))

  const toolbar = (
    <Box display='flex' alignItems='center' justifyContent='space-between' mb={2} mt={2}>
      <Box display='flex' alignItems='center'>
        {filterableColumns.length > 0 && (
          <TextField
            placeholder='Search by...'
            value={filter.value}
            onChange={onSearchInputChange}
            size='small'
            slotProps={{
              input: {
                 startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                  )
              }
            }}
            sx={{ maxWidth: '24em', mr: 2 }}
          />
        )}

        <Select
          value={filter.column === NO_FILTER_COLUMN ? '' : filter.column.id}
          onChange={onFilterColumnDropdownChange}
          size='small'
          data-testid={FILTER_DROPDOWN_TESTID}
        >
          {filterColumnDropdownOptions.map(({ label, value }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box>{toolbarActions}</Box>
    </Box>
  )

  const table = (
    <TableContainer component={Paper}>
      <Table 
        size='small' 
        sx={{ 
        minWidth: '100%', 
          '& .MuiTableCell-head': {
            fontWeight: 'bold',
            backgroundColor: '#f5f5f5'
          }
        }} 
        data-testid={ACCOUNTS_TABLE_TESTID}>
        <TableHeader columns={columns} order={order} setOrder={setOrder} />
        <TableBody>{tableRows}</TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={columns.length} align='right'>
              <Box sx={{ float: 'right' }}>
                <Pagination
                  page={activePage + 1}
                  onChange={onPageChange}
                  count={totalPages || 1}
                  color='primary'
                  shape='rounded'
                  variant="outlined"
                />
              </Box>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )

  return (
    <Container maxWidth={false} disableGutters={true}>
      {toolbar}
      {table}
    </Container>
  )
}

const TableHeader = React.memo(({ columns, order, setOrder }) => {
  // Clicking on a column makes it the "order column". If that column was
  // already the "order column", cycle between order directions (none,
  // ascending, descending). Otherwise, start at the beginning of the cycle
  // (ascending).
  const onToggleOrder = column => () => {
    const nextOrder = { ...order }

    if (column === order.column) {
      const nextIndex = nextDirectionIndex(order.directionIndex)
      if (nextIndex === 0) {
        nextOrder.column = NO_ORDER_COLUMN
      }
      nextOrder.directionIndex = nextIndex
    } else {
      nextOrder.column = column
      nextOrder.directionIndex = nextDirectionIndex(0)
    }

    setOrder(nextOrder)
  }

  const orderDirection = ORDER_DIRECTIONS[order.directionIndex]
  return (
    <TableHead>
      <TableRow className={styles.headerRow}>
        {columns.map((column, index) => (
          <TableCell key={index} onClick={column.ordering && onToggleOrder(column)} sx={column.ordering ? { cursor: 'pointer', userSelect: 'none' } : {}}>
            {column.title}
            {column === order.column && <span style={{ marginLeft: 8 }}>{orderDirection.icon}</span>}
            {column.ordering && column !== order.column && (
              <span style={{ marginLeft: 8, opacity: 0.4 }}><SortIcon fontSize='small' sx={{ verticalAlign: 'sub' }} /></span>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
})

const LoadingAccountRow = React.memo(({ columnCount }) => (
  <TableRow>
    {Array.from({ length: columnCount }).map((_value, index) => (
      <TableCell key={index}>
        <Skeleton data-testid={ACCOUNT_ROW_PLACEHOLDER_TESTID} variant='rectangular' height={24} />
      </TableCell>
    ))}
  </TableRow>
))

const FillerAccountRow = React.memo(({ columnCount }) => (
  <TableRow>
    {Array.from({ length: columnCount }).map((_value, index) => (
      <TableCell key={index}>&nbsp;</TableCell>
    ))}
  </TableRow>
))

const AccountRow = React.memo(({ account, columns, isSelected, onSelect }) => {
  return (
    <TableRow selected={isSelected} onClick={() => onSelect(account)} hover>
      {columns.map(({ id, render }, index) => (
        <TableCell {...{ [ACCOUNT_COLUMN_ID_DATA_ATTR]: id }} key={index}>
          {render(account)}
        </TableCell>
      ))}
    </TableRow>
  )
})
